import { useEffect, useRef, useState } from "react";
import { PRODUCT_NAME } from "@/config";
import { LinkedInIcon, FacebookIcon, InstagramIcon, XIcon, SubstackIcon } from "@/components/BrandIcons";
import { Check, Sparkles } from "lucide-react";
/**
 * Animation du héros : rejoue en boucle le parcours réel de l'Atelier.
 * Une idée déposée -> Pulse travaille -> trois propositions -> le visuel -> les cinq réseaux.
 *
 * Choix de mise en scène :
 *  - un projecteur se déplace d'un encart à l'autre (zoom + halo corail) pour que l'œil
 *    suive l'étape en cours sans avoir à lire ;
 *  - un curseur volontairement gros, avec une onde au clic, pour que le geste se voie.
 *
 * Contraintes tenues :
 *  - aucune promesse fausse : LinkedIn est « Publié », les autres sont « Prêt » (à coller) ;
 *  - hauteur figée, donc aucun décalage de mise en page pendant la boucle ;
 *  - respect de prefers-reduced-motion : l'état final s'affiche directement, sans mouvement ;
 *  - mise en pause quand la section sort entièrement de l'écran ;
 *  - masquée aux lecteurs d'écran, remplacée par un résumé textuel.
 */
const PHRASE = "Ce que j'ai appris en trois ans à animer un réseau d'affaires";
const PROPS = [
  { t: "L'angle « méthode »", d: "Un réseau sans méthode, c'est du networking. Voici les trois règles que j'applique…" },
  { t: "L'angle « terrain »", d: "Trois ans de rencontres m'ont appris une chose sur les réseaux d'affaires…" },
  { t: "L'angle « contre-pied »", d: "On m'a dit de multiplier les cartes de visite. J'ai fait l'inverse." },
];
const CHANS = [
  { k: "linkedin", n: "LinkedIn", I: LinkedInIcon, s: "Publié", live: true, c: "#0A66C2" },
  { k: "facebook", n: "Facebook", I: FacebookIcon, s: "Prêt", c: "#1877F2" },
  { k: "instagram", n: "Instagram", I: InstagramIcon, s: "Prêt", c: "#E4405F" },
  { k: "x", n: "X", I: XIcon, s: "Prêt", c: "#000000" },
  { k: "substack", n: "Substack", I: SubstackIcon, s: "Prêt", c: "#FF6719" },
];
/* Repères de la boucle, en millisecondes. */
const T = {
  type: 2000,
  cursor: 2100,
  press: 2750,
  release: 2950,
  props: 5000,
  prop1: 5150,
  prop2: 5700,
  prop3: 6250,
  pick: 7000,
  visual: 7900,
  chans: [9200, 9850, 10500, 11150, 11800],
  foot: 12400,
  loop: 16200,
};
const EMPTY = { typed: "", sent: false, working: false, props: false, shown: 0, picked: false, visual: false, chans: 0, foot: false, focus: "idea" };
const FINAL = { typed: PHRASE, sent: true, working: false, props: true, shown: 3, picked: true, visual: true, chans: 5, foot: true, focus: null };
/**
 * Encart mis en avant : léger zoom, halo corail, ombre portée. Les autres reculent.
 * Quand le projecteur est éteint (focus null, sur le maintien final), tout revient
 * à pleine lumière : la dernière image doit être nette, c'est celle qu'on retient.
 */
const spot = (focus, key) => {
  if (focus === key) return "transform-gpu transition-all duration-700 ease-out scale-[1.035] ring-2 ring-coral/35 shadow-[0_18px_40px_-16px_rgba(203,67,53,0.45)] relative z-10";
  if (focus === null) return "transform-gpu transition-all duration-700 ease-out scale-100 ring-0 opacity-100";
  return "transform-gpu transition-all duration-700 ease-out scale-100 ring-0 opacity-[0.72]";
};
export default function HeroAnimation() {
  const [s, setS] = useState(EMPTY);
  const [press, setPress] = useState(false);
  const [cursor, setCursor] = useState(null);
  const wrapRef = useRef(null);
  const ctaRef = useRef(null);
  const timers = useRef([]);
  const running = useRef(false);
  useEffect(() => {
    const reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) { setS(FINAL); return; }
    const at = (ms, fn) => timers.current.push(setTimeout(fn, ms));
    const clearAll = () => { timers.current.forEach(clearTimeout); timers.current = []; };
    const patch = (p) => setS((prev) => ({ ...prev, ...p }));
    const play = () => {
      clearAll();
      running.current = true;
      setS(EMPTY);
      setPress(false);
      setCursor(null);
      // 1) La frappe de l'idée.
      const stepMs = T.type / PHRASE.length;
      let i = 0;
      const type = () => {
        if (i > PHRASE.length) return;
        const n = i;
        patch({ typed: PHRASE.slice(0, n) });
        i += 1;
        timers.current.push(setTimeout(type, stepMs));
      };
      type();
      // 2) Le curseur rejoint le bouton, puis le clic.
      at(T.cursor, () => {
        const btn = ctaRef.current, wrap = wrapRef.current;
        if (!btn || !wrap) return;
        const b = btn.getBoundingClientRect(), w = wrap.getBoundingClientRect();
        setCursor({ x: b.left - w.left + b.width * 0.45, y: b.top - w.top + b.height * 0.58 });
      });
      at(T.press, () => setPress(true));
      at(T.release, () => {
        setPress(false);
        patch({ sent: true, working: true, focus: "work" });
      });
      at(T.release + 700, () => setCursor(null));
      // 3) Les trois propositions.
      at(T.props, () => patch({ working: false, props: true }));
      at(T.prop1, () => patch({ shown: 1 }));
      at(T.prop2, () => patch({ shown: 2 }));
      at(T.prop3, () => patch({ shown: 3 }));
      at(T.pick, () => patch({ picked: true }));
      // 4) Le visuel, puis les réseaux un par un.
      at(T.visual - 400, () => patch({ focus: "visual" }));
      at(T.visual, () => patch({ visual: true }));
      at(T.chans[0] - 500, () => patch({ focus: "chans" }));
      T.chans.forEach((ms, n) => at(ms, () => patch({ chans: n + 1 })));
      at(T.foot, () => patch({ foot: true, focus: null }));
      // 5) Maintien, puis reprise.
      at(T.loop, play);
    };
    play();
    /**
     * Pause hors écran. Seuil à 0 : on ne réagit qu'à une sortie complète du cadre.
     * Un seuil plus haut se déclencherait sur les variations de hauteur de la carte
     * pendant la boucle, et relancerait l'animation à contretemps.
     */
    let io;
    if (wrapRef.current && typeof IntersectionObserver !== "undefined") {
      io = new IntersectionObserver(([e]) => {
        if (e.isIntersecting) { if (!running.current) play(); }
        else if (running.current) { running.current = false; clearAll(); setS(FINAL); setCursor(null); }
      }, { threshold: 0 });
      io.observe(wrapRef.current);
    }
    return () => { clearAll(); running.current = false; if (io) io.disconnect(); };
  }, []);
  return (
    <div ref={wrapRef} className="relative" data-testid="hero-animation">
      <style>{`
        @keyframes paShine { from { transform: translateX(-110%); } to { transform: translateX(110%); } }
        @keyframes paSkeleton { to { background-position: -220% 0; } }
        @keyframes paFill { to { width: 100%; } }
        @keyframes paRipple { from { transform: scale(.2); opacity: .55; } to { transform: scale(1); opacity: 0; } }
        .pa-shine { animation: paShine 1.1s .15s ease-out 1; }
        .pa-skeleton { background: linear-gradient(100deg,#F2EFEB 30%,#E4DED7 50%,#F2EFEB 70%); background-size: 220% 100%; animation: paSkeleton 1.3s linear infinite; }
        .pa-fill { animation: paFill 2.1s cubic-bezier(.25,.7,.3,1) forwards; }
        .pa-ripple { animation: paRipple .6s ease-out forwards; }
        @media (prefers-reduced-motion: reduce) {
          .pa-shine, .pa-skeleton, .pa-fill, .pa-ripple { animation: none; }
        }
      `}</style>
      {/* Résumé pour les lecteurs d'écran : l'animation elle-même leur est masquée. */}
      <p className="sr-only">
        Démonstration : une idée est déposée dans l'Atelier, {PRODUCT_NAME} propose trois angles rédigés dans votre style,
        crée le visuel, publie sur LinkedIn et prépare la version adaptée pour Facebook, Instagram, X et Substack.
      </p>
      <div aria-hidden="true" className="rounded-3xl bg-white border border-black/5 shadow-[0_24px_70px_-20px_rgba(0,0,0,0.28)] overflow-hidden text-left">
        <div className="grid md:grid-cols-[1fr_1.05fr]">
          {/* ---------- Colonne gauche : l'Atelier ---------- */}
          <div className="p-4 md:p-6 flex flex-col gap-3 md:gap-3.5 bg-white">
            {/* Encart 1 — l'idée */}
            <div className={`rounded-2xl border border-slate-200 bg-white p-4 ${spot(s.focus, "idea")}`}>
              <span className="inline-flex items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-[0.13em] text-coral-dark bg-coral-soft px-2.5 py-1 rounded-full">
                Votre idée
              </span>
              <div className="mt-3 min-h-[62px] rounded-xl border border-slate-200 bg-[#FDFCFB] px-3 py-2.5 text-[13.5px] leading-relaxed text-ink">
                {s.typed}
                {!s.sent && <span className="inline-block w-[2px] h-[15px] align-[-3px] ml-px bg-coral-strong animate-pulse" />}
              </div>
              <button
                ref={ctaRef}
                type="button"
                tabIndex={-1}
                className={[
                  "mt-3 inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-[13.5px] font-bold transition-all duration-150",
                  s.sent ? "bg-slate-100 text-slate-400 shadow-none" : "bg-coral-strong text-white shadow-lg shadow-coral/25",
                  press ? "translate-y-[2px] scale-[.97] !bg-coral-hover" : "",
                ].join(" ")}
              >
                {s.sent ? "Demande déposée" : "Déposer la demande"}
              </button>
            </div>
            {/* Encart 2 — hauteur figée : « Pulse travaille » puis les propositions */}
            <div className={`flex-1 min-h-[172px] md:min-h-[212px] rounded-2xl border p-4 ${s.working || s.props ? "border-slate-200 bg-white" : "border-dashed border-slate-200 bg-[#FCFBFA]"} ${spot(s.focus, "work")}`}>
              {!s.working && !s.props && (
                <div className="h-full min-h-[144px] md:min-h-[180px] flex flex-col items-center justify-center text-center gap-2 text-slate-300">
                  <Sparkles className="h-6 w-6" />
                  <span className="text-[11.5px] font-semibold text-slate-400">Vos propositions apparaîtront ici</span>
                </div>
              )}
              {s.working && (
                <div>
                  <div className="flex items-start gap-3">
                    <span className="mt-0.5 h-[18px] w-[18px] flex-none rounded-full border-[2.5px] border-coral-soft border-t-coral-strong animate-spin" />
                    <p className="text-[13px] leading-snug text-ink">
                      {PRODUCT_NAME} travaille sur votre idée…
                      <span className="block mt-1 text-[11.5px] text-slate-500">
                        Vous pouvez fermer {PRODUCT_NAME}, on vous prévient quand c'est prêt.
                      </span>
                    </p>
                  </div>
                  <div className="mt-3 h-[3px] rounded-full bg-slate-100 overflow-hidden">
                    <span className="pa-fill block h-full w-0 rounded-full bg-coral-strong" />
                  </div>
                </div>
              )}
              {s.props && (
                <div className="space-y-2">
                  <p className="text-[10.5px] font-bold uppercase tracking-[0.09em] text-slate-400 mb-2">
                    Trois propositions dans votre voix
                  </p>
                  {PROPS.map((p, i) => {
                    const shown = s.shown > i;
                    const picked = s.picked && i === 0;
                    return (
                      <div
                        key={p.t}
                        className={[
                          // Sur mobile on n'en montre que deux : le héros doit rester tenable au pouce.
                          i === 2 ? "hidden md:block" : "",
                          "rounded-xl border bg-white px-3 py-2 transform-gpu transition-all duration-500",
                          shown ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2",
                          picked ? "border-coral ring-[3px] ring-coral/15 scale-[1.02]" : "border-slate-200",
                        ].join(" ")}
                      >
                        {picked && (
                          <span className="float-right ml-2 rounded-full bg-coral-soft px-2 py-0.5 text-[10px] font-bold text-coral-dark">
                            Retenue
                          </span>
                        )}
                        <span className="block text-[12.5px] font-bold text-ink">{p.t}</span>
                        <span className="block text-[11.5px] leading-snug text-slate-500 mt-0.5">{p.d}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          {/* ---------- Colonne droite : le visuel ---------- */}
          <div className="p-4 pt-0 md:p-6 md:pl-0 flex flex-col justify-center">
            <div className={`rounded-2xl border border-slate-200 bg-white overflow-hidden ${spot(s.focus, "visual")}`}>
              <div className="px-4 pt-3.5 pb-2.5">
                <span className="inline-flex items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-[0.13em] text-coral-dark bg-coral-soft px-2.5 py-1 rounded-full">
                  Le visuel, à votre image
                </span>
              </div>
              {/* Ratio 3:2, celui du fichier source : le visuel s'affiche entier, jamais rogné. */}
              <div className="relative aspect-[3/2] bg-[#F2EFEB]">
                <img
                  src="/exemple-post.jpg"
                  alt=""
                  loading="eager"
                  className={`absolute inset-0 h-full w-full object-cover transform-gpu transition-all duration-700 ease-out ${
                    s.visual ? "opacity-100 scale-100" : "opacity-0 scale-[0.96]"
                  }`}
                />
                {!s.visual && (
                  <>
                    <span className="pa-skeleton absolute inset-0" />
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-2.5 text-slate-400">
                      <span className="h-[18px] w-[18px] rounded-full border-[2.5px] border-white/70 border-t-coral-strong animate-spin" />
                      <span className="text-[11.5px] font-semibold">Le visuel se crée…</span>
                    </div>
                  </>
                )}
                {s.visual && (
                  <span className="pa-shine absolute inset-0 pointer-events-none"
                    style={{ background: "linear-gradient(105deg,transparent 38%,rgba(255,255,255,.5) 50%,transparent 62%)" }} />
                )}
              </div>
              <div className={`flex items-center gap-2 px-4 py-3 text-[11.5px] text-slate-500 border-t border-slate-100 transition-opacity duration-500 ${s.visual ? "opacity-100" : "opacity-0"}`}>
                <LinkedInIcon className="h-[14px] w-[14px] text-[#0A66C2]" />
                Publié automatiquement sur LinkedIn
              </div>
            </div>
          </div>
        </div>
        {/* ---------- Bandeau des cinq réseaux ---------- */}
        <div className="px-4 pb-5 md:px-6 md:pb-6">
          <div className={`rounded-2xl border border-slate-200 bg-[#FBFAF8] px-3 py-3 ${spot(s.focus, "chans")}`}>
            <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-2">
              {CHANS.map((c, i) => {
                const on = s.chans > i;
                return (
                  <span
                    key={c.k}
                    className={[
                      "inline-flex items-center gap-1.5 rounded-full border bg-white px-2.5 py-1.5 transform-gpu transition-all duration-500",
                      on ? "opacity-100 grayscale-0 border-slate-200 shadow-sm scale-100" : "opacity-40 grayscale border-slate-100 scale-[0.94]",
                    ].join(" ")}
                  >
                    <c.I className="h-[15px] w-[15px]" style={{ color: c.c }} />
                    <span className="text-[11.5px] font-semibold text-ink">{c.n}</span>
                    <span className={`text-[10.5px] font-semibold ${on ? (c.live ? "text-emerald-700" : "text-coral-dark") : "text-slate-400"}`}>
                      {c.s}
                    </span>
                    <span
                      className={`grid h-[14px] w-[14px] place-items-center rounded-full text-white transform-gpu transition-all duration-300 ${
                        on ? "opacity-100 scale-100" : "opacity-0 scale-50"
                      } ${c.live ? "bg-emerald-600" : "bg-coral-strong"}`}
                    >
                      <Check className="h-[8px] w-[8px]" strokeWidth={4} />
                    </span>
                  </span>
                );
              })}
            </div>
            <p className={`mt-2.5 text-center text-[11.5px] text-slate-500 transition-opacity duration-500 ${s.foot ? "opacity-100" : "opacity-0"}`}>
              LinkedIn publié automatiquement · <span className="font-bold text-ink">les quatre autres, textes et visuel prêts à coller</span>
            </p>
          </div>
        </div>
      </div>
      {/* Le curseur qui vient cliquer, volontairement large pour que le geste se voie. */}
      <div
        className="pointer-events-none absolute left-0 top-0 z-30 transition-all duration-[600ms] ease-out"
        style={{
          opacity: cursor ? 1 : 0,
          transform: `translate(${cursor ? cursor.x : 30}px, ${cursor ? cursor.y : 300}px)`,
        }}
      >
        {press && <span className="pa-ripple absolute -left-[26px] -top-[26px] h-[60px] w-[60px] rounded-full bg-coral-strong" />}
        <svg viewBox="0 0 24 24" className={`relative h-[34px] w-[34px] drop-shadow-[0_3px_6px_rgba(0,0,0,0.35)] transition-transform duration-150 ${press ? "scale-90" : "scale-100"}`}>
          <path d="M5 2.5l13.5 8.2-6.1 1.1 3.3 6.6-2.7 1.4-3.3-6.6L5 17.6z" fill="#1F1F1F" stroke="#fff" strokeWidth="1.6" strokeLinejoin="round" />
        </svg>
      </div>
    </div>
  );
}
